import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { Quote, Order, QuoteStatus, OrderStatus } from '@/types/sales';

// ─────────── Quotes ───────────

export type QuoteFilters = {
  status?: QuoteStatus;
  clientId?: string;
  search?: string;
  limit?: number;
};

export function useQuotes(filters: QuoteFilters = {}) {
  return useQuery({
    queryKey: ['quotes', filters],
    queryFn: async () => {
      const { data } = await api.get<{ items: Quote[] }>('/quotes', { params: filters });
      return data.items;
    },
  });
}

export function useQuote(id: string | undefined) {
  return useQuery({
    queryKey: ['quote', id],
    queryFn: async () => (await api.get<Quote>(`/quotes/${id}`)).data,
    enabled: !!id,
  });
}

export type SaveQuoteInput = Omit<Quote,
  'id' | 'number' | 'createdAt' | 'createdBy' | 'updatedAt' | 'updatedBy' | 'totals' | 'sentAt' | 'acceptedDate' | 'convertedOrderId'
> & { totals?: Quote['totals'] };

export function useCreateQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SaveQuoteInput) =>
      (await api.post<{ id: string; number: string }>('/quotes', input)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['quotes'] }),
  });
}

export function useUpdateQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...patch }: { id: string } & Partial<SaveQuoteInput>) =>
      (await api.patch(`/quotes/${id}`, patch)).data,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['quotes'] });
      qc.invalidateQueries({ queryKey: ['quote', vars.id] });
    },
  });
}

export function useUpdateQuoteStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: QuoteStatus }) =>
      (await api.patch(`/quotes/${id}/status`, { status })).data,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['quotes'] });
      qc.invalidateQueries({ queryKey: ['quote', vars.id] });
    },
  });
}

export function useDeleteQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.delete(`/quotes/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['quotes'] }),
  });
}

export function useConvertQuoteToOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, poNumber }: { id: string; poNumber?: string }) =>
      (await api.post<{ id: string; number: string }>(`/quotes/${id}/convert`, { poNumber })).data,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['quotes'] });
      qc.invalidateQueries({ queryKey: ['quote', vars.id] });
      qc.invalidateQueries({ queryKey: ['orders'] });
    },
  });
}

// ─────────── Orders ───────────

export type OrderFilters = {
  status?: OrderStatus;
  clientId?: string;
  search?: string;
  limit?: number;
};

export function useOrders(filters: OrderFilters = {}) {
  return useQuery({
    queryKey: ['orders', filters],
    queryFn: async () => (await api.get<{ items: Order[] }>('/orders', { params: filters })).data.items,
  });
}

export function useOrder(id: string | undefined) {
  return useQuery({
    queryKey: ['order', id],
    queryFn: async () => (await api.get<Order>(`/orders/${id}`)).data,
    enabled: !!id,
  });
}

export type SaveOrderInput = Omit<Order,
  'id' | 'number' | 'createdAt' | 'createdBy' | 'updatedAt' | 'updatedBy' | 'totals' | 'milestones' | 'shippedDate' | 'deliveredDate'
> & { totals?: Order['totals'] };

export function useCreateOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SaveOrderInput) =>
      (await api.post<{ id: string; number: string }>('/orders', input)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orders'] }),
  });
}

export function useUpdateOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...patch }: { id: string } & Partial<SaveOrderInput>) =>
      (await api.patch(`/orders/${id}`, patch)).data,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['orders'] });
      qc.invalidateQueries({ queryKey: ['order', vars.id] });
    },
  });
}

export function useUpdateOrderStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: OrderStatus }) =>
      (await api.patch(`/orders/${id}/status`, { status })).data,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['orders'] });
      qc.invalidateQueries({ queryKey: ['order', vars.id] });
    },
  });
}

export function useDeleteOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.delete(`/orders/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orders'] }),
  });
}
